(ns job-monitoring-dashboard.ui
  (:require ["@js-temporal/polyfill$Temporal.Duration" :as Duration]
            ["@js-temporal/polyfill$Temporal.Instant" :as Instant]
            ["@js-temporal/polyfill$Temporal.Now" :as Now]
            ["@js-temporal/polyfill$Temporal.ZonedDateTime" :as ZonedDateTime]
            [clojure.string :as str]
            [fipp.edn :as fipp]
            [kitchen-async.promise :as p]
            [lambdaisland.fetch :as fetch]
            [medley.core :as medley]
            [reagent.core :as r]
            [reagent.dom :as dom]))

(def job-groups
  {"adwords-poller" [:target_object]
   "basket-case-redshift" [:input]
   "dw-import" [:target_system :target_object]
   "event-pipeline-post-processor" [:target_system :input]
   "giosg-poller-redshift" [:input]
   "giosg-poller-snowflake" [:input]
   "kiikari-indexer" [:target_object]
   "kiikari-sibbe-transfer" [:input]
   "profile-publisher" [:input]
   "redshift-archiver" [:target_object]
   "redshift-exporter" [:input]
   "sibbe-redshift-replication" [:target_object]
   "snowflake-archiver" [:target_object]
   "sql-runner" [:input]
   "survey-import" [:target_system]})

(def *only-failures? (r/atom true))

(def *render-visible-elements? (r/atom false))
(def *render-hidden-elements? (r/atom false))

(defonce *config (r/atom nil))

(def dev-config
  {#_#_:api-url "/fake-api"
   :api-url "https://32357j6bl5.execute-api.eu-west-1.amazonaws.com/api"
   :login-url "https://job-monitoring-dashboard-dev.auth.eu-west-1.amazoncognito.com/login?client_id=1ka9drectptbufjn66m0t8g9ql&response_type=token&scope=aws.cognito.signin.user.admin+email+openid+profile&redirect_uri=http://localhost:8080"})

(defn fetch-config! []
  (p/let [response (fetch/get "/config.json")
          config (if (= 200 (:status response))
                   (-> (:body response)
                       (js->clj :keywordize-keys true))
                   dev-config)]
    (reset! *config config)))

(defn navigate-to-login! []
  (let [{:keys [login-url]} @*config]
    (when login-url
      (.assign js/window.location login-url)
      (p/promise [_resolve _reject])))) ; block the next steps with a promise which is never resolved

(defn authenticate! []
  (let [hash (subs js/location.hash 1)
        params (when-not (str/blank? hash)
                 (js/URLSearchParams. hash))
        error (when params
                (.get params "error"))
        error-description (when params
                            (.get params "error_description"))
        new-access-token (when params
                           (.get params "access_token"))
        old-access-token (.getItem js/localStorage "access-token")]
    (cond
      (some? error) (throw (ex-info "Authentication failed"
                                    {:error error
                                     :error-description error-description}))
      (some? new-access-token) (do
                                 (swap! *config assoc :access-token new-access-token)
                                 (.setItem js/localStorage "access-token" new-access-token)
                                 (.replaceState js/window.history nil "" "/")) ; hide the access token from the URL
      (some? old-access-token) (swap! *config assoc :access-token old-access-token)
      :else (navigate-to-login!))))

(defn- parse-api-response [{:keys [body status]}]
  (let [body (js->clj body :keywordize-keys true)]
    (case status
      200 body
      (throw (js/Error. (str status " " body))))))

(defn- sleep [ms]
  (p/promise [resolve]
    (js/setTimeout #(resolve) ms)))

(defn api-get! [url]
  (p/loop [retries [500 1000 2000 3000 3000 3000]]
    (p/let [{:keys [api-url access-token]} @*config
            response (fetch/get (str api-url url)
                                {:headers {"Authorization" (str "Bearer " access-token)}})
            status (:status response)
            [retry-ms & retries] retries]
      (cond
        (= 401 status)
        (navigate-to-login!)

        (and (not= 200 status)
             (some? retry-ms))
        (p/do
          (js/console.warn (str "GET " api-url " returned " status ", will retry in " retry-ms " ms"))
          (sleep retry-ms)
          (p/recur retries))

        :else
        (parse-api-response response)))))

(defn handle-s3-redirect! [data]
  (if-some [redirect (:s3-redirect data)]
    (p/let [response (fetch/get redirect)]
      (parse-api-response response))
    data))


(defonce *data (r/atom nil))

(defn parse-timestamp [s]
  (when s
    (Instant/from s)))

(defn enrich-jobs [jobs]
  (let [jobs (->> jobs
                  (mapv (fn [job]
                          (-> job
                              (update :start_time parse-timestamp)
                              (update :end_time parse-timestamp)))))
        start (->> jobs
                   (map :end_time)
                   (filter some?)
                   (apply min-key #(.-epochMilliseconds %)))
        end (->> (concat (map :start_time jobs)
                         (map :end_time jobs))
                 (filter some?)
                 (apply max-key #(.-epochMilliseconds %)))]
    {:jobs jobs
     :start start
     :end end}))

(defn fetch-data! []
  (reset! *data {:loading? true})
  (p/try
    (p/let [data (api-get! "/jobs.json")
            data (handle-s3-redirect! data)
            #_#_data (update data :jobs #(take 100 %))]
      (reset! *data (enrich-jobs (:jobs data))))
    (p/catch :default e
      (js/console.log e)
      (reset! *data {:error e}))))

(defn- soon [f]
  (js/setTimeout f 100))

(defn incremental-render! []
  (r/flush) ; show "Rendering..." status text
  (soon (fn []
          (reset! *render-visible-elements? true)
          (r/flush) ; show default jobs (i.e. only failures)
          (soon (fn []
                  ;; create hidden DOM nodes for non-default jobs
                  (reset! *render-hidden-elements? true))))))


(defn timeline-position [^js/Temporal.Instant timestamp
                         ^js/Temporal.Instant start
                         ^js/Temporal.Instant end]
  (if timestamp
    (let [total (-> (.until start end)
                    (.total #js {"unit" "second"}))
          offset (-> (.until start timestamp)
                     (.total #js {"unit" "second"}))]
      (str (* 100 (/ offset total)) "%"))
    "100%"))

(defn timeline-time-markers [^js/Temporal.Instant start
                             ^js/Temporal.Instant end
                             timezone]
  (let [start (.toZonedDateTimeISO start timezone)
        end (.toZonedDateTimeISO end timezone)
        day-starts (->> (.round start #js {"smallestUnit" "day"
                                           "roundingMode" "floor"})
                        (iterate #(.add % #js {"days" 1}))
                        (take-while #(neg? (ZonedDateTime/compare % end))))
        hour-starts (->> (.round start #js {"smallestUnit" "hour"
                                            "roundingMode" "floor"})
                         (iterate #(.add % #js {"hours" 1}))
                         (take-while #(neg? (ZonedDateTime/compare % end)))
                         (filter #(contains? #{6 12 18} (.-hour %))))]
    [:<>
     (into [:<>] (for [day-start day-starts]
                   [:div.timeline-date {:style {:left (timeline-position day-start start end)}}]))
     (into [:<>] (for [hour-start hour-starts]
                   [:div.timeline-hour {:style {:left (timeline-position hour-start start end)}}]))
     [:div.timeline-now {:style {:right 0}}]
     [:div.timeline-labels
      (into [:<>] (for [^js/Temporal.ZonedDateTime day-start day-starts]
                    [:div.timeline-date {:style {:left (timeline-position day-start start end)}}
                     [:span (str (.toPlainDate day-start))]]))
      (into [:<>] (for [^js/Temporal.ZonedDateTime hour-start hour-starts]
                    [:div.timeline-hour {:style {:left (timeline-position hour-start start end)}}
                     [:span (str (-> (.toPlainTime hour-start)
                                     (.toString #js {"smallestUnit" "minute"})))]]))
      [:div.timeline-now {:style {:right 0}}]]]))

(defn- aws-console-url-encode [url]
  (-> url
      (str/replace "/" "$252F")))

(defn- duration-between ^js/Temporal.Duration [^js/Temporal.Instant start
                                               ^js/Temporal.Instant end]
  (.until start
          (or end (Now/instant))
          #js {"largestUnit" "hour"
               "smallestUnit" "second"}))

(defn job-execution [job start end]
  (let [start-pos (timeline-position (:start_time job) start end)
        end-pos (timeline-position (:end_time job) start end)]
    [:div.job-execution {:class (str "status-" (:job_status job))
                         :title (with-out-str
                                  (fipp/pprint (-> job
                                                   (update :end_time #(some-> % str))
                                                   (update :start_time #(some-> % str))
                                                   (assoc :duration (str (duration-between (:start_time job)
                                                                                           (:end_time job))))
                                                   (->> (into (sorted-map))))))
                         :style {:left start-pos
                                 :width (str "calc(" end-pos " - " start-pos ")")}
                         :on-click (fn [_event]
                                     (if (:log_stream job)
                                       (js/window.open (str "https://" (:region job) ".console.aws.amazon.com/cloudwatch/home?region=" (:region job)
                                                            "#logsV2:log-groups/log-group/" (aws-console-url-encode (:log_group job))
                                                            "/log-events/" (aws-console-url-encode (:log_stream job)))
                                                       "_blank")
                                       (js/alert "No log stream")))}
     (case (:job_status job)
       "success" "✅"
       "failure" "❌"
       "started" "⏳"
       "⚠️")]))

(def max-jobs-per-row 1000) ; safety guard to avoid DoS due to rendering too many jobs

(defn job-executions-row [group jobs start end]
  [:div.job-executions-row
   [:div.job-executions-row-title
    (str group)
    (when (< max-jobs-per-row (count jobs))
      [:span.too-many-jobs
       (str " Too many jobs. Showing only the latest "
            max-jobs-per-row " out of " (count jobs) ".")])]
   (for [job (take-last max-jobs-per-row jobs)]
     ^{:key (:job_id job)} [job-execution job start end])])

(defn- count-statuses [jobs]
  (->> jobs
       (map :job_status)
       (frequencies)
       (medley/map-keys keyword)))

(defn job-category [job-name jobs start end]
  (let [{:keys [failure started]} (count-statuses jobs)
        visible? (or (not @*only-failures?)
                     (pos? failure)
                     (pos? started))]
    (when (if visible?
            @*render-visible-elements?
            @*render-hidden-elements?)
      [:div.job-category {:style {;; toggling the display property is faster than adding the elements to DOM
                                  :display (if visible?
                                             "block"
                                             "none")}}
       [:div.job-category-title
        (str job-name
             " (" (count jobs) " jobs"
             (when (pos? failure)
               (str ", " failure " failures"))
             (when (pos? started)
               (str ", " started " started"))
             ")")]
       (doall
        (for [[groups jobs] (if-some [groups (get job-groups job-name)]
                              (->> jobs
                                   (group-by (apply juxt groups))
                                   (sort-by first))
                              [[nil jobs]])]
          (let [group (str/join "/" groups)
                {:keys [failure started]} (count-statuses jobs)]
            [:div {:key group
                   :style {:display (if (or (not @*only-failures?)
                                            (pos? failure)
                                            (pos? started))
                                      "block"
                                      "none")}}
             [job-executions-row group jobs start end]])))])))

(defn timeline-jobs [jobs start end]
  [:<>
   (for [[job-name jobs] (->> jobs
                              (group-by :job_name)
                              (sort-by first))]
     ^{:key job-name} [job-category job-name jobs start end])])

(defn timeline [jobs start end]
  (when (and start end)
    [:div.timeline-wrapper
     [:div.timeline
      [timeline-time-markers start end "Europe/Helsinki"]
      [timeline-jobs jobs start end]]]))

(defn app []
  (let [{:keys [loading? error jobs start end]} @*data]
    [:<>
     [:header
      [:h1 "Job Monitoring Dashboard"]
      (cond
        loading? [:p "Loading..."]
        error [:p "Failed. " (str error)]
        (not @*render-visible-elements?) [:p "Rendering..."]
        :else [:<>
               [:p "From: " (str start)]
               [:p "Until: " (str end)]
               [:p [:label
                    [:input {:type "checkbox"
                             :checked @*only-failures?
                             :on-change #(reset! *only-failures? (-> % .-target .-checked))}]
                    " Show only failures?"]]])]
     (when @*render-visible-elements?
       [timeline jobs start end])]))

(defn error-page [exception]
  [:<>
   [:header
    [:h1 "Error"]
    [:p (str (ex-message exception))]
    (into [:<>] (for [[k v] (ex-data exception)]
                  [:p
                   [:b (name k) ": "]
                   (str v)]))
    [:a {:href "/"}
     "Retry"]]])

(defn init! []
  (let [root (.getElementById js/document "root")]
    (dom/render [app] root)
    (p/try
      (p/do
        (fetch-config!)
        (authenticate!)
        (fetch-data!)
        (incremental-render!))
      (p/catch :default e
        (dom/render [error-page e] root)))))

(init!)

(defn ^:dev/before-load stop [])

(defn ^:dev/after-load start []
  (init!))
